import React, { FC } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@mui/material";

import { DateInput, TextInput } from "@/components/atoms";
import { RefRadioButtonGroup, RefSelectInput } from "@/components/molecules";

import { formSchema } from "./formSchema";

export interface GeneralInformationFormTypes {
    personalTitle: string;
    lastName: string;
    firstName: string;
    gender: string;
    maritalStatus: string;
    birthdate: Date;
    birthPlace: string;
    nationality: string;
    language: string;
}

type GeneralInformationFormProps = {
    isSubmittable?: boolean;
    personDetails: MyBrokerApi.PersonalDetails;
    onSubmit: (values: MyBrokerApi.PersonalDetails) => void;
};

const defaultValues = (personDetails: MyBrokerApi.PersonalDetails) => {
    return {
        personalTitle: personDetails?.personalTitle?.id || "",
        lastName: personDetails?.lastName,
        firstName: personDetails?.firstName,
        gender: personDetails?.gender?.id || "",
        maritalStatus: personDetails?.maritalStatus?.id || "",
        birthdate: personDetails?.birthdate || null,
        birthPlace: personDetails?.birthPlace,
        nationality: personDetails?.nationality?.id || "",
        language: personDetails?.language?.id || "",
    };
};

const formMapper = (formData: GeneralInformationFormTypes): MyBrokerApi.PersonalDetails => {
    return {
        ...formData,
        personalTitle: { id: formData.personalTitle },
        gender: { id: formData.gender },
        maritalStatus: { id: formData.maritalStatus },
        nationality: { id: formData.nationality },
        language: { id: formData.language },
    };
};

const GeneralInformationForm: FC<GeneralInformationFormProps> = ({
    personDetails = null,
    isSubmittable = true,
    onSubmit,
}) => {
    const { t } = useTranslation();
    const {
        control,
        handleSubmit,
        formState: { isDirty },
    } = useForm<GeneralInformationFormTypes>({
        mode: "onBlur",
        defaultValues: defaultValues(personDetails),
        resolver: yupResolver(formSchema()),
    });

    const submitHandler = (formData: GeneralInformationFormTypes) => {
        onSubmit(formMapper(formData));
    };

    return (
        <form onSubmit={handleSubmit(submitHandler)}>
            <RefSelectInput name="personalTitle" required control={control} table="personTitle" />
            <TextInput name="lastName" label={t("Common.Name")} required autoComplete="off" control={control} />
            <TextInput name="firstName" label={t("Common.FirstName")} required autoComplete="off" control={control} />
            <RefRadioButtonGroup name="gender" table="gender" control={control} required />
            <RefSelectInput name="maritalStatus" required control={control} table="maritalStatus" />
            <DateInput name="birthdate" control={control} disableFuture label={t("Common.BirthDate")} />
            <TextInput name="birthPlace" label={t("Common.BirthPlace")} autoComplete="off" control={control} />
            <RefSelectInput name="nationality" control={control} table="nationality" />
            <RefSelectInput name="language" required control={control} table="language" />
            <Button disabled={!isDirty || !isSubmittable} type="submit" className="mt10" fullWidth>
                {t("User.PersonalDetails.Submit")}
            </Button>
        </form>
    );
};

export default GeneralInformationForm;
